import { useEffect, useState } from 'react'
import { CircularProgress, Stack, Typography } from '@mui/material'
import { CommentOutlined } from '@mui/icons-material'
import { apiGet } from '../../generic/Api_Functions'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { SmartDialog } from '../../generic/utilities/SmartDialog'
import createDialogTitle from '../../generic/utilities/dialogUtil'
import AlertUI from '../../generic/AlertUI'

const CLOSED = { loaded: false }

/**
 * Story 188 (CU6/CU7): version and comment of the last registered save of one workflow version.
 * Shared by the selection tables of the Workflows Manager (row menu) and Balance (row button).
 *
 * @param {Object} props
 * @param {number} props.workflowId - Id of the version to show; 0 keeps the dialog closed
 * @param {Function} props.onClose - Called when the dialog closes (Close button or failed request)
 */
const WorkflowLastSaveCommentDialog = ({ workflowId = 0, onClose }) => {
  const { t_ } = useSmartTranslation()
  const [AlertElement, showAlert] = AlertUI()
  const [lastSaveComment, setLastSaveComment] = useState(CLOSED)

  // Fetched on every opening (RN18): the host resets workflowId to 0 on close, so each id change is
  // a fresh request. Only workflowId drives the effect: onClose is an inline arrow of the host.
  useEffect(() => {
    setLastSaveComment(CLOSED)
    if (workflowId === 0) return
    let current = true
    apiGet('workflows/' + workflowId + '/lastSaveComment')
      .then((response) => { if (current) setLastSaveComment({ ...response, loaded: true }) })
      .catch(() => {
        if (!current) return
        showAlert({ severity: 'error', title: t_('Error getting the last save comment') })
        onClose()
      })
    return () => { current = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflowId])

  return (
    <>
      {AlertElement}
      <SmartDialog
        setOpen={workflowId !== 0}
        title={createDialogTitle({ title: t_('Last save comment'), icon: CommentOutlined })}
        accept={false}
        cancel={false}
        close
        closeCallback={onClose}
        renderComponent={
          lastSaveComment.loaded
            ? (
              <Stack spacing={2} sx={{ minWidth: '24rem', padding: (theme) => theme.spacing(2) }} data-testid='workflow-last-save-comment'>
                <Typography><strong>{t_('Version')}:</strong> {lastSaveComment.versionedName}</Typography>
                <Typography component='div'>
                  <strong>{t_('Comment')}:</strong>
                  <Typography sx={{ whiteSpace: 'pre-wrap', marginTop: '0.5rem' }} data-testid='workflow-last-save-comment-text'>
                    {lastSaveComment.comment || t_('No comment')}
                  </Typography>
                </Typography>
              </Stack>
              )
            : <CircularProgress data-testid='workflow-last-save-comment-loading' />
        }
      />
    </>
  )
}

export default WorkflowLastSaveCommentDialog
